import type { DomainAdapterConfig } from "@repo/adapter-definitions/domain-com-au";
import {
  DOMAIN_LISTING_TYPES,
  DOMAIN_PROPERTY_TYPES,
  DOMAIN_SORT_OPTIONS,
} from "@repo/adapter-definitions/domain-com-au";
import { Badge } from "@repo/ui/badge";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/select";
import { Switch } from "@repo/ui/switch";

interface DomainConfigPanelProps {
  config: DomainAdapterConfig;
  onChange: (config: DomainAdapterConfig) => void;
}

export function DomainConfigPanel({ config, onChange }: DomainConfigPanelProps) {
  const update = (partial: Partial<DomainAdapterConfig>) => {
    onChange({ ...config, ...partial });
  };

  const listingType = config.listingType || "any";
  const isSold = listingType === "sold";
  const sortListingType = listingType === "any" ? "buy" : listingType;

  const togglePropertyType = (value: string) => {
    const current = config.propertyTypes || [];
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    update({ propertyTypes: next.length > 0 ? next : undefined });
  };

  const sortOptions = DOMAIN_SORT_OPTIONS.filter((option) =>
    (option.forTypes as readonly string[]).includes(sortListingType),
  );

  return (
    <div className="space-y-4 rounded-lg border bg-muted/30 p-3">
      <div className="flex items-center gap-2 text-sm font-medium">Domain.com.au Configuration</div>

      <div className="space-y-1.5">
        <Label htmlFor="domain-listing-type" className="text-xs">
          Listing Type
        </Label>
        <Select
          value={listingType}
          onValueChange={(value) =>
            update({ listingType: value as DomainAdapterConfig["listingType"] })
          }
        >
          <SelectTrigger id="domain-listing-type" className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DOMAIN_LISTING_TYPES.map((listing) => (
              <SelectItem key={listing.value} value={listing.value}>
                {listing.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Property Types (leave empty for all)</Label>
        <div className="flex flex-wrap gap-1.5">
          {DOMAIN_PROPERTY_TYPES.slice(0, 8).map((propertyType) => (
            <Badge
              key={propertyType.value}
              variant={config.propertyTypes?.includes(propertyType.value) ? "default" : "outline"}
              className="cursor-pointer py-0.5 text-xs"
              onClick={() => togglePropertyType(propertyType.value)}
            >
              {propertyType.label}
            </Badge>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="domain-min-bedrooms" className="text-xs">
            Min Bedrooms
          </Label>
          <Select
            value={config.minBedrooms?.toString() || "any"}
            onValueChange={(value) =>
              update({ minBedrooms: value === "any" ? undefined : Number.parseInt(value, 10) })
            }
          >
            <SelectTrigger id="domain-min-bedrooms" className="h-8 text-sm">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              {[1, 2, 3, 4, 5].map((value) => (
                <SelectItem key={value} value={value.toString()}>
                  {value}+
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="domain-max-bedrooms" className="text-xs">
            Max Bedrooms
          </Label>
          <Select
            value={config.maxBedrooms?.toString() || "any"}
            onValueChange={(value) =>
              update({ maxBedrooms: value === "any" ? undefined : Number.parseInt(value, 10) })
            }
          >
            <SelectTrigger id="domain-max-bedrooms" className="h-8 text-sm">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              {[1, 2, 3, 4, 5, 6].map((value) => (
                <SelectItem key={value} value={value.toString()}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="domain-min-price" className="text-xs">
            Min Price
          </Label>
          <Input
            id="domain-min-price"
            type="number"
            placeholder="e.g. 500000"
            className="h-8 text-sm"
            value={config.minPrice || ""}
            onChange={(event) =>
              update({
                minPrice: event.target.value ? Number.parseInt(event.target.value, 10) : undefined,
              })
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="domain-max-price" className="text-xs">
            Max Price
          </Label>
          <Input
            id="domain-max-price"
            type="number"
            placeholder="e.g. 2000000"
            className="h-8 text-sm"
            value={config.maxPrice || ""}
            onChange={(event) =>
              update({
                maxPrice: event.target.value ? Number.parseInt(event.target.value, 10) : undefined,
              })
            }
          />
        </div>
      </div>

      {isSold && (
        <div className="flex items-center justify-between">
          <Label className="text-xs">Exclude price withheld</Label>
          <Switch
            checked={config.excludePriceWithheld !== false}
            onCheckedChange={(checked) => update({ excludePriceWithheld: checked })}
          />
        </div>
      )}

      {sortOptions.length > 0 && (
        <div className="space-y-1.5">
          <Label htmlFor="domain-sort-by" className="text-xs">
            Sort By
          </Label>
          <Select
            value={config.sortBy || "default"}
            onValueChange={(value) => update({ sortBy: value === "default" ? undefined : value })}
          >
            <SelectTrigger id="domain-sort-by" className="h-8 text-sm">
              <SelectValue placeholder="Default" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1.5 border-t pt-2">
        <Label htmlFor="domain-manual-url" className="text-xs text-muted-foreground">
          Manual URL Override (paste a Domain search URL as fallback)
        </Label>
        <Input
          id="domain-manual-url"
          placeholder="https://www.domain.com.au/sold-listings/..."
          className="h-8 font-mono text-sm"
          value={config.manualUrl || ""}
          onChange={(event) => update({ manualUrl: event.target.value || undefined })}
        />
      </div>
    </div>
  );
}
